'use client';

import type * as React from 'react';

import { SilkSheet } from '@/components/ui/silk-sheet';

/**
 * Waitlist Rewards prize-pool Terms & Conditions, opened from the inline
 * "terms and conditions" link inside the prize-breakdown modal's raffle note.
 *
 * Legal copy is hardcoded English by deliberate convention — it mirrors
 * `src/content/legal/terms.tsx` / `privacy.tsx` (which carry no `useTranslations`)
 * so the binding wording is never machine-translated. The clause set is the
 * protective core, drafted as dense, self-contained boilerplate: the prize pool
 * is a discretionary, illustrative promotion, with no binding offer and no claim
 * if a Mainnet Launch does not occur or the distribution is not run. It is
 * deliberately standalone (no cross-links into the main app Terms/Privacy) and
 * carries its own governing-law, liability, and release provisions.
 */
export function PrizeTermsModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}): React.ReactNode {
  return (
    <SilkSheet
      open={open}
      onOpenChange={onOpenChange}
      variant="detached"
      size="lg"
      title="Waitlist Rewards — Terms & Conditions"
    >
      <div
        data-testid="prize-terms-content"
        className="flex flex-col gap-5 text-foreground-secondary"
      >
        <p className="text-caption leading-relaxed text-foreground-tertiary">
          These Waitlist Rewards Terms and Conditions (these “Terms”) are entered into by and
          between the operator of the Morfi service (the “Sponsor”, “we”, “us” or “our”) and each
          individual who participates in the Waitlist Rewards programme (the “Participant”, “you” or
          “your”). The Waitlist Rewards programme, including its points, levels, streaks,
          leaderboard, referral mechanics, content rewards, raffle and promotional prize pool
          (collectively, the “Programme”), is a promotional activity offered at the Sponsor’s
          discretion. By accessing, enrolling in, participating in, or accruing points under the
          Programme, the Participant acknowledges that the Participant has read, understood and
          irrevocably agrees to be bound by these Terms. The Sponsor may amend these Terms at any
          time with effect from the posting of the amended version, and continued participation
          constitutes acceptance thereof.
        </p>

        <Clause title="1. Nature of the promotion">
          The Programme, the prize pool, the leaderboard distributions and the raffle (collectively,
          the “Rewards”) are promotional, gratuitous and entirely discretionary in nature, and do
          not, and shall not be construed to, constitute an offer, contract, guarantee, undertaking
          or binding promise of any kind. For the avoidance of doubt, any monetary amounts, point
          values, tiers, payout tables, raffle odds, prize quantities, dates, pool totals or similar
          figures displayed within the service, in the prize breakdown, in marketing materials or in
          these Terms are indicative and illustrative only, and confer no right, interest or
          entitlement upon any Participant.
        </Clause>

        <Clause title="2. Condition precedent; Mainnet Launch">
          Notwithstanding anything to the contrary herein, the distribution of any Rewards is
          expressly conditioned upon the Sponsor achieving a public launch of its protocol on the
          Solana mainnet (a “Mainnet Launch”) and being able to proceed with such distribution. The
          Participant acknowledges and agrees that a Mainnet Launch is contingent upon numerous
          factors beyond the Sponsor’s assured control, including, without limitation, the
          availability of sufficient funding, the satisfactory completion of security audits, the
          receipt of any requisite legal or regulatory clearances, technical readiness and
          prevailing market conditions, and that the Sponsor makes no representation, warranty or
          guarantee that a Mainnet Launch will occur. In the event that a Mainnet Launch does not
          occur, or is or becomes impractical, unsafe, unlawful or commercially unviable, the
          Sponsor shall be under no obligation whatsoever to distribute any Rewards, and no
          Participant shall have any claim, right, entitlement, demand or recourse of any nature
          against the Sponsor or its affiliates in connection therewith. Nothing in the service,
          these Terms or any communication of the Sponsor or its personnel shall constitute a
          binding commitment to achieve a Mainnet Launch, to operate or complete the Programme, or
          to pay or deliver any amount to any person.
        </Clause>

        <Clause title="3. Reservation of rights">
          The Sponsor reserves the right, in its sole and absolute discretion and to the maximum
          extent permitted by applicable law, to modify, suspend, shorten, extend, delay, reduce,
          restructure, discontinue or cancel the Programme, the Rewards, any amounts, payout tables,
          odds and eligibility criteria, and any point-accrual, weighting, quality-scoring or
          distribution methodology, at any time and without notice or liability.
        </Clause>

        <Clause title="4. No consideration; not a lottery">
          Participation in the Programme is and shall remain free of charge. No purchase, payment,
          trade, stake, deposit or provision of anything of value is required to participate, to
          accrue points, or to be eligible for any Reward, and no such act shall improve a
          Participant’s prospects. The Programme requires no consideration and is not, and shall not
          be construed as, a lottery, gaming or gambling activity.
        </Clause>

        <Clause title="5. Status of points">
          Points, ranks, levels, streaks and raffle entries constitute a revocable promotional
          record only. They have no cash or monetary value, do not constitute money, property,
          securities or any financial instrument, confer no ownership, equity or proprietary
          interest, are non-transferable, and are not redeemable for cash, tokens or anything of
          value save as the Sponsor may determine in its sole discretion. The Sponsor may adjust,
          recalculate, reset, expire, freeze or void points at any time, including to correct error,
          fraud or abuse.
        </Clause>

        <Clause title="6. Eligibility">
          Eligibility to participate is conditional upon the Participant being at least eighteen
          (18) years of age (or the age of majority in the Participant’s jurisdiction, whichever is
          greater); not being resident in, or accessing the Programme from, any jurisdiction in
          which the Programme is restricted or prohibited or which is subject to comprehensive
          sanctions or embargo; not being a person designated on any applicable sanctions or
          restricted-party list; and holding and connecting a valid X account and a Morfi account,
          with one account per natural person. The Programme is void where prohibited by law. The
          Participant is solely responsible for compliance with all laws applicable to the
          Participant. The Sponsor may introduce or vary eligibility criteria and excluded
          jurisdictions at any time.
        </Clause>

        <Clause title="7. Verification; disqualification; forfeiture">
          Any Reward is conditional upon the recipient (a) being an eligible Participant; (b)
          completing identity verification (KYC) and sanctions screening to the Sponsor’s
          satisfaction; (c) verifying control of the relevant account and wallet; and (d) claiming
          within such period as the Sponsor may specify. The Sponsor shall be the sole and final
          arbiter of eligibility, point validity and Reward entitlement, and reserves the right to
          audit any account and to exclude, disqualify, void points for, or withhold or claw back
          Rewards from any account that is frozen, flagged, fraudulent, automated, duplicative,
          self-dealing or otherwise in breach of these Terms. Rewards allocated to ineligible or
          non-claiming recipients may be redrawn or retained. Unclaimed Rewards are forfeited.
        </Clause>

        <Clause title="8. Taxes; form of reward">
          Each recipient is solely responsible for any and all taxes, duties and reporting
          obligations arising in connection with a Reward. The Sponsor may withhold amounts and
          issue tax documentation where required, and may condition delivery of a Reward upon
          receipt of required tax information. All amounts are indicative and stated before any
          applicable fees, network costs or taxes; the Sponsor may satisfy any Reward in USDC,
          another token or another form, or substitute a Reward of equal or greater value, in its
          discretion.
        </Clause>

        <Clause title="9. No securities; no reliance">
          The Programme is a promotional activity. The points, Rewards and any tokens are not
          offered as, and do not constitute, securities, investment contracts or financial
          instruments, and participation carries no expectation of profit derived from the efforts
          of others. Nothing herein constitutes investment, financial, legal, accounting or tax
          advice, and no Participant shall rely upon any statement of the Sponsor as such.
        </Clause>

        <Clause title="10. Disclaimers; limitation of liability; release">
          The Programme is provided on an “as is” and “as available” basis. To the maximum extent
          permitted by applicable law, the Sponsor disclaims all warranties, conditions and
          representations, whether express or implied, and all liability arising out of or in
          connection with the Programme, and the Sponsor’s aggregate liability arising out of or
          relating to the Programme shall not exceed the minimum amount permitted under applicable
          law. To the maximum extent permitted by applicable law, the Participant hereby releases,
          waives and forever discharges the Sponsor and its affiliates, officers, directors,
          employees, agents and personnel from any and all claims, demands, losses and liabilities
          of every kind arising out of or relating to the Programme, the Rewards, the points, the
          Participant’s participation, and any decision not to achieve a Mainnet Launch or not to
          distribute any Rewards.
        </Clause>

        <Clause title="11. Governing law; dispute resolution">
          These Terms, and any dispute or claim arising out of or in connection with them or their
          subject matter (including non-contractual disputes or claims), shall be governed by and
          construed in accordance with the laws applicable to the Sponsor, without regard to
          conflict-of-laws principles. To the maximum extent permitted by applicable law, any such
          dispute shall be resolved on an individual basis, and each Participant waives any right to
          participate in any class, collective or representative proceeding and any right to a trial
          by jury. The English-language version of these Terms shall prevail.
        </Clause>

        <Clause title="12. General">
          These Terms constitute the entire agreement between the parties in respect of the
          Programme and supersede all prior or contemporaneous understandings relating thereto. No
          failure or delay by the Sponsor in exercising any right shall operate as a waiver thereof.
          If any provision of these Terms is held to be invalid or unenforceable, that provision
          shall be severed and the remaining provisions shall continue in full force and effect. The
          Sponsor may assign or transfer its rights and obligations hereunder. Headings are for
          convenience only and shall not affect interpretation.
        </Clause>

        <p className="text-caption text-foreground-tertiary">
          Enquiries:{' '}
          <a
            href="mailto:legal@morfi.markets"
            className="underline underline-offset-2 transition-colors hover:text-foreground-primary"
          >
            legal@morfi.markets
          </a>
          .
        </p>
      </div>
    </SilkSheet>
  );
}

function Clause({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): React.ReactNode {
  return (
    <section className="flex flex-col gap-1.5">
      <h3 className="text-heading-xs text-foreground-primary">{title}</h3>
      <p className="text-caption leading-relaxed">{children}</p>
    </section>
  );
}
